import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import { resolveNavigationScreenToURL } from '@gitbook/app-navigation';
import {
    CONTENT_DEFAULT_BRANCH,
    getRevisionCreationTime,
    type RevisionInfo,
} from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { createPalette, DatePrettyLocale, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard, confirm, Description, Link, toast } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { useSiteContentPreviewURL } from '../SitePreview';

/**
 * Return the command palette for a revision.
 */
export function useRevisionCommands(
    space: api.Space,
    revision: RevisionInfo,
    changeRequest: api.ChangeRequest | null,
    // when the CR has been fetched by a parent, we can pass it here to avoid refetching it
    options?: { readOnly?: boolean }
): () => PaletteDef {
    const hiveClient = useHiveClient();
    const { navigate } = useNavigation();
    const previewURL = useSiteContentPreviewURL({
        space,
        revisionId: revision.id,
        changeRequestNumber: changeRequest?.number,
        pagePath: '',
    });

    const branchId = changeRequest
        ? getChangeRequestBranchId(changeRequest.id)
        : CONTENT_DEFAULT_BRANCH;

    /** Is this revision the latest on the change request or space */
    const isCurrent = changeRequest
        ? changeRequest.revision === revision.id
        : space.revision === revision.id;

    /** Test if the revision can be rollbacked */
    const canRollback =
        !options?.readOnly &&
        !isCurrent &&
        (!changeRequest ||
            changeRequest.status === api.ChangeRequestStatus.Open ||
            changeRequest.status === api.ChangeRequestStatus.Draft);

    const palette = () => {
        return createPalette({
            groups: [
                canRollback
                    ? {
                          key: 'revision',
                          commands: [
                              {
                                  key: 'revision.rollback',
                                  icon: Icon.RollBack,
                                  title: 'Revert to this revision',
                                  trigger: () => {
                                      void confirm({
                                          kind: 'danger',
                                          title: `Revert ${changeRequest ? `change request #${changeRequest.number}` : 'space'} to this point in time`,
                                          description: (
                                              <>
                                                  Your {changeRequest ? 'change request' : 'space'}{' '}
                                                  will go back to this point in time. The entire
                                                  content will be replaced by the state of the
                                                  content as of "
                                                  <DatePrettyLocale
                                                      value={getRevisionCreationTime(revision)}
                                                      showTime
                                                      className="font-bold"
                                                  />
                                                  ". No history will be lost.
                                              </>
                                          ),
                                          children: previewURL ? (
                                              <Description>
                                                  You can preview this point in time at{' '}
                                                  <Link href={previewURL} target="_blank">
                                                      {previewURL}
                                                  </Link>
                                              </Description>
                                          ) : null,

                                          confirmLabel: 'Revert',
                                          confirmLeadingIcon: Icon.RollBack,
                                          onConfirm: async () => {
                                              const promise = hiveClient.rollbackBranch(
                                                  space.id,
                                                  branchId,
                                                  {
                                                      toRevisionId: revision.id,
                                                  }
                                              );

                                              toast.promise(promise, {
                                                  loading: 'Rolling back to the revision…',
                                                  success: 'Content rolled back to the revision',
                                                  error: 'Error while rolling back to the revision',
                                                  description: 'No history will be lost.',
                                              });

                                              await promise;

                                              analytics.logEvent(
                                                  analytics.createEditEvent(
                                                      space,
                                                      changeRequest?.id,
                                                      {
                                                          type: 'edit_rollback_revision',
                                                      }
                                                  )
                                              );

                                              navigate({
                                                  screen: {
                                                      revision: undefined,
                                                      mode: undefined,
                                                  },
                                              });
                                          },
                                      });
                                  },
                              },
                          ],
                      }
                    : null,
                previewURL
                    ? {
                          key: 'preview',
                          commands: [
                              {
                                  key: 'revision.preview',
                                  icon: Icon.DocsSite,
                                  title: 'Preview in new tab',
                                  trigger: () => {
                                      window.open(previewURL, '_blank');
                                  },
                              },
                              {
                                  key: 'revision.copyPreviewURL',
                                  icon: Icon.Copy,
                                  title: 'Copy preview URL',
                                  trigger: () => {
                                      clipboard.writeText(previewURL, {
                                          notify: { title: 'Preview URL copied to clipboard' },
                                      });
                                  },
                              },
                          ],
                      }
                    : null,
                {
                    key: 'copy',
                    commands: [
                        {
                            key: 'revision.copyURL',
                            icon: Icon.Link,
                            title: 'Copy URL',
                            trigger: () => {
                                clipboard.writeText(
                                    resolveNavigationScreenToURL({
                                        type: 'space',
                                        space: revision.contentId,
                                        revision: revision.id,
                                        page: '',
                                        organization: space.organization,
                                    }),
                                    { notify: { title: 'Revision URL copied to clipboard' } }
                                );
                            },
                        },
                    ],
                },
            ].filter(filterOutNullable),
        });
    };

    return palette;
}
